<template>
  <div class="offer-schedule-selection-container">
    <title-description
      ref="title-description-maria-form"
      :status="campaign.status"
      :title.sync="title"
      :counter="titleDescriptionCounter"
      :description.sync="description"
      :title-error="booleanTitleErrorComputed"
      :title-error-message="titleErrorComputed"
      :description-error="booleanDescriptionErrorComputed"
      :description-error-message="descriptionErrorComputed"
      :disable-title="isMariaCampaignFinished"
      :disable-description="isMariaCampaignFinished"
      use-card-title
    />
    <mf-card-container
      v-if="isFranchisee && hasOfferSalesPermission"
      v-model="searchFranchiseeOffers"
      title="Seleção de ofertas da rede"
      description="Para começar a configuração de sua campanha, selecione as ofertas que você deseja comunicar seus consumidores. Não é possível selecionar ofertas com segmentos diferentes ou com datas de validade com mais de 7 dias de diferença."
      :use-search="showSearch"
      placeholder="Buscar"
    >
      <div class="card-container-content">
        <tabs-selection :tab="0" :disabled="isDisabled(true)" :franchisee="isFranchisee" :search="searchFranchiseeOffers" />
      </div>
    </mf-card-container>
    <mf-card-container v-model="searchOffers" :title="offersTitle" :description="offersDescription" :use-search="showSearch" placeholder="Buscar">
      <div class="card-container-content">
        <tabs-selection :tab.sync="tab" :disabled="isDisabled(false)" :search="searchOffers" :initially-products="initiallyProducts" />
      </div>
    </mf-card-container>
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import TabsSelection from './TabsSelection.vue'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'

import { mariaCampaign } from '../../../mixins/maria-campaign'

export default {
  name: 'OfferScheduleSelection',
  components: {
    TabsSelection,
    TitleDescription,
  },
  mixins: [mariaCampaign],
  props: {
    initiallyProducts: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    search: '',
    tab: 0,
    disabledFranchisee: false,
    searchOffers: '',
    searchFranchiseeOffers: '',
    tryingToSaveAsDraft: false,
  }),
  computed: {
    titleDescriptionCounter() {
      return {
        title: 70,
        description: 150,
      }
    },
    offersTitle() {
      return !this.isFranchisee ? 'Seleção de ofertas' : 'Minhas ofertas'
    },
    offersDescription() {
      return !this.isFranchisee
        ? 'Para começar a configuração de sua campanha, selecione as ofertas que você deseja comunicar seus consumidores. Não é possível selecionar ofertas com segmentos diferentes ou com datas de validade com mais de 7 dias de diferença.'
        : ''
    },
    titleErrorComputedTeste() {
      return this.titleErrorComputed
    },
    title: {
      get() {
        return this.campaign.title
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'title', payload: value })
      },
    },
    description: {
      get() {
        return this.campaign.description
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'description', payload: value })
      },
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
    showSearch() {
      return this.tab === 0 && this.hasOfferSalesPermission
    },
    booleanTitleErrorComputed() {
      return Boolean(this.$v.campaign?.title?.$error)
    },
    titleErrorComputed() {
      if (this.$v.campaign.title) {
        const { $error, required, minLength, maxLength, notOnlySpace } = this.$v.campaign.title

        if ($error) {
          if (!required) return 'Dê um nome para esta campanha.'
          if (!minLength) return 'O título possui menos de 2 caracteres.'
          if (!maxLength) return 'O título possui mais de 70 caracteres.'
          if (!notOnlySpace) return 'Não é permitido salvar apenas espaços.'
        }
        return ''
      }
      return ''
    },
    booleanDescriptionErrorComputed() {
      return Boolean(this.$v.campaign?.description?.$error)
    },
    descriptionErrorComputed() {
      if (this.$v.campaign.description) {
        const { $error, required, maxLength, notOnlySpace } = this.$v.campaign.description

        if ($error) {
          if (!required) return 'Informe detalhes para melhor identificação da campanha.'
          if (!maxLength) return 'A descrição possui mais de 150 caracteres.'
          if (!notOnlySpace) return 'Não é permitido salvar apenas espaços.'
        }
        return ''
      }
      return ''
    },
  },
  watch: {
    hasFranchiseeOfferSelected(value) {
      if (value) this.tab = 0
    },
    tab() {
      this.searchFranchiseeOffers = ''
      this.searchOffers = ''
    },
  },
  methods: {
    isDisabled(isFranchisee) {
      if (isFranchisee && this.searchedProducts.concat(this.importedProducts).length > 0) return true
      let hasFranchiseItems = false

      this.campaign.offer_schedules.map((offer) => {
        if (offer?.from_franchise) {
          hasFranchiseItems = true
        }
      })

      return this.isMariaCampaignFinished || (!isFranchisee && hasFranchiseItems)
    },
    validateStepOneFormData() {
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.goToErrorField()
        return false
      }

      let allSelectedProducts = this.campaign.offer_sales

      const selectedProductsSet = new Set()

      allSelectedProducts.forEach((product) => {
        if (product._id) selectedProductsSet.add(product._id)
        if (product.groupInfo?._id) selectedProductsSet.add(product.groupInfo._id)
      })

      const allSearchedAndImportedProducts = this.searchedProducts.concat(this.importedProducts).map((product) => {
        return { ...product, active: product?.active ?? true, selectedEans: product.eans }
      })

      let filteredProducts = [...allSelectedProducts]

      allSearchedAndImportedProducts.forEach((product) => {
        const productIds = [product._id, product.groupInfo?._id].filter(Boolean)

        const existsInSelected = productIds.every((id) => selectedProductsSet.has(id))

        if (!existsInSelected) {
          filteredProducts.push(product)
        }
      })

      this.setOmniCampaignKey({
        key: 'offer_sales',
        payload: filteredProducts,
      })

      if (this.campaign.offer_sales.length > 1000) {
        this.$snackbar({ message: 'Por questões de performance, não é permitido selecionar mais de 1000 produtos nessa fase.', snackbarColor: 'error' })
        return false
      }

      if (!this.campaign.offer_schedules.length && !this.campaign.offer_sales.length && !this.tryingToSaveAsDraft) {
        this.$snackbar({ message: 'Selecione uma oferta ou um produto.', snackbarColor: 'error' })
        return false
      }

      return true
    },
  },
  validations() {
    if (this.tryingToSaveAsDraft) {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            maxLength: maxLength(70),
            notOnlySpace,
          },
        },
      }
    } else {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            maxLength: maxLength(70),
            notOnlySpace,
          },
          description: {
            required,
            maxLength: maxLength(150),
          },
        },
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.offer-schedule-selection-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  .card-custom-search-header {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    gap: 56px;
  }

  .card-container-content {
    margin-top: 24px;
  }

  ::v-deep .card-container-container {
    align-items: center !important;
    .card-container-actions {
      vertical-align: middle;
      width: 400px;
      height: 100%;
      margin-left: 5%;
      .d-flex {
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
